import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Film, Utensils, Gamepad2, Bike, BookOpen, Video, Palette,
  UtensilsCrossed, Heart, EyeOff, Trash2, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FamilyActivity } from '@/types/familyBonding';
import { cn } from '@/lib/utils';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  film: Film,
  utensils: Utensils,
  'gamepad-2': Gamepad2,
  bike: Bike,
  'book-open': BookOpen,
  video: Video,
  palette: Palette,
  'utensils-crossed': UtensilsCrossed,
  heart: Heart,
};

interface FamilyActivityItemProps {
  activity: FamilyActivity;
  isCompleted: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  onRemove?: () => void;
}

export function FamilyActivityItem({
  activity,
  isCompleted,
  onToggle,
  onDismiss,
  onRemove,
}: FamilyActivityItemProps) {
  const IconComponent = iconMap[activity.icon] || Heart;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg transition-all duration-200',
        isCompleted
          ? 'bg-primary/10 border border-primary/20'
          : 'bg-muted/50 hover:bg-muted border border-transparent'
      )}
    >
      <Checkbox
        checked={isCompleted}
        onCheckedChange={onToggle}
        className="h-5 w-5"
      />
      
      <div className={cn(
        'p-2 rounded-full',
        isCompleted ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
      )}>
        <IconComponent className="h-4 w-4" />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={cn(
          'font-medium text-sm truncate',
          isCompleted && 'line-through text-muted-foreground'
        )}>
          {activity.title}
        </p>
        {activity.description && (
          <p className="text-xs text-muted-foreground truncate">
            {activity.description}
          </p>
        )}
      </div>

      {activity.isCustom && (
        <span className="text-[10px] px-1.5 py-0.5 bg-accent text-accent-foreground rounded-full">
          Custom
        </span>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Activity options">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onDismiss}>
            <EyeOff className="h-4 w-4 mr-2" />
            Hide for now
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={activity.isCustom && onRemove ? onRemove : onDismiss}
            className="text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
